import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { callFunction } from '../../firebase/functions';
import { signOut } from '../../firebase/auth';
import LanguageChip from '../../components/shared/LanguageChip';
import { isPlyrmatchDomain } from '../../utils/portalDomain';
import { PlyrmatchLogo, plyrmatchButtonStyle, plyrmatchPageStyle } from '../../components/auth/PlyrmatchBrand';

function inviteRoleLabel(inviteData, t) {
  if (!inviteData) return '';
  if (inviteData.role === 'academyAdmin') return t('auth:acceptInvite.roleAcademyAdmin', 'an Academy Admin');
  if (inviteData.role === 'salesRep') return t('auth:acceptInvite.roleSalesRep', 'a Sales Consultant');
  if (inviteData.role === 'coach') return t('auth:acceptInvite.roleCoach', 'a Coach');
  if (inviteData.role === 'iapSales') return t('auth:acceptInvite.roleIapSales', 'IAP Sales Staff');
  if (inviteData.role === 'guardian') {
    // This account can belong to a player managing their own portal just as
    // easily as a parent/guardian — only the academyPortal-only case (the
    // Academy Portal's own player invite flow) lets us say "Player" for
    // sure; otherwise stay neutral rather than assuming "Guardian".
    const access = inviteData.access || [];
    if (access.includes('academyPortal') && !access.includes('playerPortal')) return t('auth:acceptInvite.rolePlayer', 'a Player');
    return t('auth:acceptInvite.rolePlayerGuardian', 'a Player/Guardian');
  }
  return t('auth:acceptInvite.rolePlayerGuardian', 'a Player/Guardian');
}

export default function AcceptInvite() {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState('loading'); // loading | form | done | error
  const [inviteData, setInviteData] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const validateInvite = callFunction('validateInviteToken');
    validateInvite({ token })
      .then(({ data }) => {
        setInviteData(data);
        setStep('form');
      })
      .catch(() => setStep('error'));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError(t('auth:acceptInvite.passwordsDontMatch', 'Passwords do not match.'));
      return;
    }
    if (password.length < 8) {
      setError(t('auth:acceptInvite.passwordTooShort', 'Password must be at least 8 characters.'));
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const acceptInvite = callFunction('acceptInvite');
      await acceptInvite({ token, password });
      // acceptInvite only creates the account server-side — it never signs
      // this browser into it. If this tab already had an active session
      // (very easy to hit during QA: a staff member testing several invite
      // links in the same browser), Login.jsx's "already signed in, skip
      // the form" redirect would otherwise bounce straight back into that
      // OLD session instead of ever prompting for the new account's
      // credentials, with no visible error — the new account exists but
      // was never actually reachable from this tab.
      try { await signOut(); } catch { /* no session to sign out of — fine */ }
      setStep('done');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.message || t('auth:acceptInvite.failedToAccept', 'Failed to accept invite. Please contact your administrator.'));
    } finally {
      setSubmitting(false);
    }
  };

  const onPlyrmatchDomain = isPlyrmatchDomain();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 relative" style={onPlyrmatchDomain ? plyrmatchPageStyle : undefined}>
      <div className="absolute top-4 right-4">
        <LanguageChip variant="tailwind" />
      </div>
      <div className="w-full max-w-sm">
        {onPlyrmatchDomain ? (
          <PlyrmatchLogo />
        ) : (
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">{t('auth:acceptInvite.title', 'Academy Portal')}</h1>
            <p className="mt-1 text-sm text-gray-500">{t('auth:acceptInvite.subtitle', 'Accept your invitation')}</p>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          {step === 'loading' && (
            <p className="text-center text-sm text-gray-500">{t('auth:acceptInvite.validating', 'Validating invite…')}</p>
          )}

          {step === 'error' && (
            <div className="text-center space-y-2">
              <p className="text-sm font-medium text-red-700">{t('auth:acceptInvite.invalidTitle', 'This invite link is invalid or has expired.')}</p>
              <p className="text-sm text-gray-500">{t('auth:acceptInvite.invalidBody', 'Please contact your administrator for a new invite.')}</p>
            </div>
          )}

          {step === 'done' && (
            <div className="text-center space-y-2">
              <p className="text-sm font-medium text-green-700">{t('auth:acceptInvite.successTitle', 'Account created successfully!')}</p>
              <p className="text-sm text-gray-500">{t('auth:acceptInvite.successBody', 'Redirecting to login…')}</p>
            </div>
          )}

          {step === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {inviteData && (
                <div className="rounded-md bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800">
                  {inviteData.role === 'iapSales' ? (
                    // No academy on this invite (iapSales is platform-level,
                    // like superAdmin) — the normal "join {academyName}"
                    // phrasing doesn't apply.
                    t('auth:acceptInvite.invitedAsIapSales', "You've been invited to join the International Academy Portal team as IAP Sales Staff.")
                  ) : (
                    <>
                      {t('auth:acceptInvite.invitedAsPrefix', "You've been invited to join")} <strong>{inviteData.academyName}</strong> {t('auth:acceptInvite.invitedAsSuffix', 'as {{role}}.', { role: inviteRoleLabel(inviteData, t) })}
                    </>
                  )}
                </div>
              )}

              {error && (
                <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth:acceptInvite.setPassword', 'Set password')}</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth:acceptInvite.confirmPassword', 'Confirm password')}</label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                style={onPlyrmatchDomain ? plyrmatchButtonStyle : undefined}
                className="w-full rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? t('auth:acceptInvite.creatingAccount', 'Creating account…') : t('auth:acceptInvite.createAccount', 'Create account')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
